'use client';

import { c as _c } from "react/compiler-runtime";
import type { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useRef } from 'react';
const PARALLAX_TWEEN_FACTOR_BASE = 0.1;
const PARALLAX_INPUT_RANGE_PERCENT = 9.5;
const PARALLAX_MAX_TRANSLATE_PERCENT = 3.25;
const PARALLAX_MAX_SCALE = 1.19;
const getParallaxTranslate = (rawTranslate: number) => {
  const normalized = Math.max(-1, Math.min(1, rawTranslate / PARALLAX_INPUT_RANGE_PERCENT));
  const eased = normalized + (normalized ** 3 - normalized ** 5) / 2;
  return eased * PARALLAX_MAX_TRANSLATE_PERCENT;
};
export const useFeatureParallax = (emblaApi, isEnabled, onSwipeProgress) => {
  const $ = _c(12);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const stageRefs = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const imageRefs = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const secondaryImageRefs = useRef(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (index, stage) => {
      stageRefs.current[index] = stage;
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const setStageRef = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (index_0, image) => {
      imageRefs.current[index_0] = image;
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const setImageRef = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (index_1, image_0) => {
      secondaryImageRefs.current[index_1] = image_0;
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const setSecondaryImageRef = t5;
  let t6;
  let t7;
  if ($[6] !== emblaApi || $[7] !== isEnabled || $[8] !== onSwipeProgress) {
    t6 = () => {
      if (!emblaApi || !isEnabled) {
        return;
      }
      const stages = stageRefs.current;
      const images = imageRefs.current;
      const secondaryImages = secondaryImageRefs.current;
      let frame = null;
      let engine = emblaApi.internalEngine();
      let scrollSnaps = emblaApi.scrollSnapList();
      let tweenFactor = PARALLAX_TWEEN_FACTOR_BASE * scrollSnaps.length;
      const syncParallaxData = api => {
        engine = api.internalEngine();
        scrollSnaps = api.scrollSnapList();
        tweenFactor = PARALLAX_TWEEN_FACTOR_BASE * scrollSnaps.length;
      };
      const updateParallax = api_0 => {
        const scrollProgress = api_0.scrollProgress();
        const slidesInView = new Set(api_0.slidesInView());
        scrollSnaps.forEach((scrollSnap, snapIndex) => {
          let diffToTarget = scrollSnap - scrollProgress;
          engine.slideRegistry[snapIndex].forEach(slideIndex => {
            if (!slidesInView.has(slideIndex)) {
              return;
            }
            if (engine.options.loop) {
              engine.slideLooper.loopPoints.forEach(loopPoint => {
                const target = loopPoint.target();
                if (slideIndex !== loopPoint.index || target === 0) {
                  return;
                }
                const direction = Math.sign(target);
                if (direction === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (direction === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              });
            }
            const stage_0 = stages[slideIndex];
            const image_1 = images[slideIndex];
            if (!stage_0 || !image_1) {
              return;
            }
            const translate = getParallaxTranslate(diffToTarget * tweenFactor * 100);
            image_1.style.translate = `${translate}% 0`;
            if (secondaryImages[slideIndex]) {
              secondaryImages[slideIndex].style.translate = `${translate}% 0`;
            }
            stage_0.style.scale = `${1 + Math.abs(translate) / PARALLAX_MAX_TRANSLATE_PERCENT * (PARALLAX_MAX_SCALE - 1)}`;
            onSwipeProgress?.(slideIndex, Math.abs(translate) / PARALLAX_MAX_TRANSLATE_PERCENT);
          });
        });
      };
      const scheduleParallax = api_1 => {
        if (frame !== null) {
          return;
        }
        frame = window.requestAnimationFrame(() => {
          frame = null;
          updateParallax(api_1);
        });
      };
      const handleReInit = api_2 => {
        if (frame !== null) {
          window.cancelAnimationFrame(frame);
        }
        frame = null;
        syncParallaxData(api_2);
        updateParallax(api_2);
      };
      const handleSettle = api_3 => {
        if (frame !== null) {
          window.cancelAnimationFrame(frame);
        }
        frame = null;
        updateParallax(api_3);
      };
      updateParallax(emblaApi);
      emblaApi.on("reInit", handleReInit);
      emblaApi.on("scroll", scheduleParallax);
      emblaApi.on("settle", handleSettle);
      return () => {
        if (frame !== null) {
          window.cancelAnimationFrame(frame);
        }
        stages.forEach(_temp);
        images.forEach(_temp2);
        secondaryImages.forEach(_temp3);
        emblaApi.off("reInit", handleReInit);
        emblaApi.off("scroll", scheduleParallax);
        emblaApi.off("settle", handleSettle);
      };
    };
    t7 = [emblaApi, isEnabled, onSwipeProgress];
    $[6] = emblaApi;
    $[7] = isEnabled;
    $[8] = onSwipeProgress;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      setStageRef,
      setImageRef,
      setSecondaryImageRef
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
};
function _temp(stage_1) {
  return stage_1?.style.removeProperty("scale");
}
function _temp2(image_2) {
  return image_2?.style.removeProperty("translate");
}
function _temp3(image_3) {
  return image_3?.style.removeProperty("translate");
}